import { z } from "zod";

import { OrgRole, OrgRoleLiteral } from "./enums.js";
import { zGuard } from "./zod/utils.js";
import { KernelSize } from "./kernelConfigTypes.js";
import { AiCreditLimit, FREE_TIER_USER_CREDITS } from "./magic/credits.js";
import { getNormalEnum } from "./pseudoEnums.js";
import { typedObjectKeys } from "./utils/typedObjects.js";

/*eslint sort-keys: ["error", "asc", {caseSensitive: false, natural: true, allowLineSeparatedGroups: true}] -- expect object keys to be sorted*/
/**
 * FeatureGateSet expresses a set of features that are disabled/enabled
 * for a given product pricing plan, ie. Community, Pro, Enterprise plans.
 *
 * This should not be used to define disabled/enabled features for specific orgs
 * outside the scope of which pricing plan the org is on.
 */
const FeatureGateSetBase = {
  name: z.string(),

  /** Project view graph analytics */
  advancedProjectViewStats: z.boolean(),
  /**
   * Admins can disable the ability for users to create data connections
   * from anywhere outside of the admin panel. Turning this on does not
   * break existing data connections, but prevents new ones from being created.
   */
  allowDisableAddDataConnectionOutsideAdminPanel: z.boolean(),
  /** Admins can disable CSV downloads for App-only users at the org level */
  allowDisableCsvDownload: z.boolean(),
  /**
   * Admins can disable viewing workspace secrets in the projects UI.
   * Yes, it is trivial to expose secrets via code, but this prevents
   * accidental exposure in the UI.
   */
  allowDisableViewWorkspaceSecretsInProjectUI: z.boolean(),
  allowDisableWorkspaceSharing: z.boolean(),
  /** Admins can disable file uploads across all projects at the org level */
  allowRestrictFileUploads: z.boolean(),
  /** App kernel idle timeout in milliseconds */
  appIdleTimeout: z.number(),
  auditLogs: z.boolean(),
  /** Admins can configure automatic arhcive criteria */
  autoArchive: z.boolean(),
  collections: z.boolean(),
  components: z.boolean(),
  configurableKernelSize: z.boolean(),
  contextStudio: z.boolean(),
  /** Admins can set per-user/group/workspace AI credit usage limits */
  creditUsageLimits: z.boolean(),
  customAppIcon: z.boolean(),
  /** custom code formatting (e.g. sqlfluff) is enabled */
  customCodeFormatting: z.boolean(),
  /** Admins can configure custom color palettes */
  customColorPalettes: z.boolean(),
  customCronScheduledRuns: z.boolean(),
  /** Admins can customize role request messages or disable role requests */
  customOrgRoleRequests: z.boolean(),
  /** Number of custom published app themes. Null indicates this feature is disabled */
  customPublishedAppThemeLimit: z.number().or(z.null()),
  /** Admins can configure query permissions for shared data connections */
  databaseQueryPermissions: z.boolean(),
  /** Whether or not an org can configure and use data retention policies */
  dataRetention: z.boolean(),
  dbtIntegration: z.boolean(),
  dbtMetricCell: z.boolean(),
  /** Default kernel size for new projects */
  defaultKernelSize: KernelSize,
  /** Default Logic kernel idle timeout in milliseconds; for maximum value, see maxFreeLogicIdleTimeout */
  defaultLogicIdleTimeout: z.number(),
  directorySync: z.boolean(),
  /** number of days of edit history viewable */
  editHistoryDays: z.number(),
  /** An org can designate statuses as endorsed */
  endorsements: z.boolean(),
  /** Explore from a published app is enabled */
  exploreFromHere: z.boolean(),
  /** Explore from any table is enabled */
  exploreFromScratch: z.boolean(),
  /** Ability to assign the explorer role to workspace members */
  explorerRole: z.boolean(),
  /** Access to the public Hex API */
  externalApi: z.boolean(),
  externalFileIntegrations: z.boolean(),
  evals: z.boolean(),
  /** Whether or not the GitHub integration (i.e. package import) is active */
  githubIntegration: z.boolean(),
  /** Whether or not GitHub sync repository and GitLab sync project support is active */
  githubSync: z.boolean(), // used for both github and gitlab sync
  /** Whether or not ad-hoc Google Sheets export is available from display tables */
  googleSheetsExport: z.boolean(),
  /** Whether or not the org can create groups */
  groups: z.boolean(),
  hexMagic: z.boolean(),
  /** Whether or not the org can use their own API key for magic */
  magicApiKey: z.boolean(),
  /**Whether magic typeahead is active */
  magicTypeahead: z.boolean(),
  /** Maximum Logic kernel idle timeout for free compute profiles in milliseconds; for default value, see defaultLogicIdleTimeout */
  maxFreeLogicIdleTimeout: z.number(),
  /**
   * Any compute profile with a memory limit larger than this `maxKernelSizes` compute profile will be shown with a tooltip
   * which compute profiles are shown at all is determined by the `kernelSizesForClient` variable
   */
  maxKernelSizes: KernelSize,
  /** Kernel max execution timeout in milliseconds  */
  maxRuntime: z.number(),
  /** Whether or not the org can access the MCP server */
  mcp: z.boolean(),
  /** Whether or not the org can use Hex as an MCP client */
  mcpClient: z.boolean(),
  /** Whether or not an org can use publish reviews at all */
  optionalPublishReviews: z.boolean(),
  /** Whether or not org domains can be used to auto add users */
  orgDomains: z.boolean(),
  /** Whether or not org-level files can be created and used */
  orgFiles: z.boolean(),
  /**
   * Whether or not a project (ie. Hex) can be shared to the org
   *
   * @deprecated
   */
  orgProjectSharing: z.boolean(),
  /** Whether or not org-level secrets can be created and used */
  orgSecrets: z.boolean(),
  /** Whether to allow users to upgrade their kernels to paid compute profiles */
  paidCompute: z.boolean(),
  /** Whether presentation mode is available for published apps */
  presentationMode: z.boolean(),
  /** Whether or not an org can require publish reviews */
  requiredPublishReviews: z.boolean(),
  /**
   * Whether or not saved views can be accessed / created.
   */
  savedViews: z.boolean(),
  scheduledRunLimit: z.number(),
  /** Whether or not scheduled runs will execute */
  scheduledRuns: z.boolean(),
  /** Whether scheduled runs can be managed from the admin UI */
  scheduledRunsManager: z.boolean(),
  /** Whether or not schedules can be configured to include notifications */
  scheduleNotifications: z.boolean(),
  /** Includes both scheduled run screenshots and on-demand PDF downloads */
  screenshots: z.boolean(),
  /** Whether an org can create semantic projects or not */
  semanticProjects: z.boolean(),
  /** Whether or not server git providers (e.g. GHE Server) are active */
  serverGitProviders: z.boolean(),
  /**
   * Max number of collections sharable with workspace or users + groups, per org, null means infinite. Set to 0 to disable sharing.
   * Value will be disregarded if `collections` feature set field is false
   */
  sharedCollectionLimit: z.number().or(z.null()),
  /**
   * Max number of components sharable with workspace or users + groups, per org, null means infinite. Set to 0 to disable sharing.
   * Value will be disregarded if `components` feature set field is false
   */
  sharedComponentLimit: z.number().or(z.null()),
  /** Whether or not scheduled run notifications can be sent to Slack channels */
  slackChannelNotifications: z.boolean(),
  snowflakeOAuthConnections: z.boolean(),
  /** Whether or not SSO (not OAuth) is enabled or configurable */
  sso: z.boolean(),
  /**
   * Whether or not more than 5 categories/statuses can be created
   *
   * @deprecated - no longer checked in FE
   */
  unlimitedProjectLabels: z.boolean(),
  usersLastActive: z.boolean(),
  /** number of days of version history viewable, null means infinite */
  versionHistoryDays: z.number().or(z.null()),
  /** Whether view data for a chart is enabled */
  viewData: z.boolean(),
  /** Can admins see analytics for workspace assets */
  workspaceUsageAnalytics: z.boolean(),
};
/*eslint-disable -- expect object keys to be sorted*/

export const FeatureGateSet = z.object(FeatureGateSetBase);
export type FeatureGateSet = z.infer<typeof FeatureGateSet>;

export type FeatureGateUpgradeToTierMapping = Record<
  keyof FeatureGateSet,
  FeatureGateTier | null
>;

export type FeatureGate = keyof FeatureGateSet;

export const FeatureGateAddons = z.enum([
  "signedEmbedding",
  "agentUsageLimit",
  "appAgent",
]);
export type FeatureGateAddons = z.infer<typeof FeatureGateAddons>;

// Add ons are paid additional features that are added to base plans that might not map to a specific tier
export type FeatureGateWithAddons = FeatureGate | FeatureGateAddons;

/**
 * This is the user facing feature gate description used:
 * - To show the user in the contact sales dialog form
 * - To create the a human readable campaign name in salesforce when first seen
 *
 * If a feature gate has a contact sales upsell, it must be registered here.
 */
export const FeatureGateDescriptionForSalesUpsell = {
  auditLogs: "audit logs",
  directorySync: "directory sync",
  allowDisableWorkspaceSharing: "disable workspace sharing",
  allowDisableCsvDownload: "disable CSV downloads",
  allowRestrictFileUploads: "restrict file uploads",
  allowDisableViewWorkspaceSecretsInProjectUI:
    "disable viewing workspace secrets",
  allowDisableAddDataConnectionOutsideAdminPanel:
    "disable non-admin data connection creation",
  customOrgRoleRequests: "customize role request messages",
  customPublishedAppThemeLimit: "additional custom app themes",
  snowflakeOAuthConnections: "OAuth connections",
  databaseQueryPermissions: "database query permissions",
  signedEmbedding: "signed embedding",
  explorerRole: "the explorer role",
  magicApiKey: "use your own OpenAI API key",
  sso: "enable SSO",
  agentUsageLimit: "increase your AI usage limit",
} as const;
export type SalesUpsellFeatureGates =
  keyof typeof FeatureGateDescriptionForSalesUpsell;
export const FeatureGateSalesUpsellOverridesForDialog: Partial<
  Record<
    SalesUpsellFeatureGates,
    {
      title: string;
      description: string;
    }
  >
> = {
  agentUsageLimit: {
    title: "Request more credits",
    description: "Contact our sales team to inquire about extended agent usage",
  },
};

export const SalesUpsellFeatureGateNameWithoutAddons = z.union(
  typedObjectKeys(FeatureGateDescriptionForSalesUpsell)
    .filter((k) => !zGuard(FeatureGateAddons, k))
    .map((k) => z.literal(k)) as any,
) as z.ZodType<
  Exclude<keyof typeof FeatureGateDescriptionForSalesUpsell, FeatureGateAddons>
>;

export const FeatureGateName = z.union(
  typedObjectKeys(FeatureGateSetBase).map((k) => z.literal(k)) as any,
) as z.ZodType<keyof typeof FeatureGateSetBase>;

export const FeatureGateNameWithAddons = z.union([
  FeatureGateName,
  FeatureGateAddons,
]);

export const FeatureGateTierLiteral = z.enum([
  "COMMUNITY",
  "PROFESSIONAL",
  "TEAM",
  "ENTERPRISE",
]);
export type FeatureGateTier = z.infer<typeof FeatureGateTierLiteral>;
export const FeatureGateTier = getNormalEnum(FeatureGateTierLiteral);

export const LABELS_BY_FEATURE_GATE_TIER: Record<FeatureGateTier, string> = {
  [FeatureGateTier.COMMUNITY]: "Community",
  [FeatureGateTier.ENTERPRISE]: "Enterprise",
  [FeatureGateTier.TEAM]: "Team",
  [FeatureGateTier.PROFESSIONAL]: "Professional",
};

export const SEAT_PRICE_BY_FEATURE_GATE_TIER: Record<
  FeatureGateTier,
  number | null
> = {
  [FeatureGateTier.COMMUNITY]: 0,
  [FeatureGateTier.ENTERPRISE]: null,
  [FeatureGateTier.TEAM]: 75,
  [FeatureGateTier.PROFESSIONAL]: 36,
};

export const ROLES_WITH_DEFAULT_AI_CREDITS = [
  OrgRole.ADMIN,
  OrgRole.MANAGER,
  OrgRole.EDITOR,
  OrgRole.EXPLORER,
] as const;

export type OrgRoleWithDefaultAiCredits =
  (typeof ROLES_WITH_DEFAULT_AI_CREDITS)[number];

const rolesWithDefaultAiCredits = new Set<OrgRole>(
  ROLES_WITH_DEFAULT_AI_CREDITS,
);

export const isOrgRoleWithDefaultAiCredits = (
  orgRole: OrgRole,
): orgRole is OrgRoleWithDefaultAiCredits =>
  rolesWithDefaultAiCredits.has(orgRole);

/**
 * User-level AI credit limits by feature gate tier and eligible org role.
 * UNLIMITED_AI_CREDITS_SENTINEL indicates unlimited credits for that tier/role combination.
 * These are monthly limits that apply per individual user.
 */
export const USER_CREDITS_BY_FEATURE_TIER_AND_ROLE: Record<
  FeatureGateTier,
  Record<OrgRoleWithDefaultAiCredits, AiCreditLimit>
> = {
  [FeatureGateTier.COMMUNITY]: {
    [OrgRole.ADMIN]: FREE_TIER_USER_CREDITS,
    [OrgRole.MANAGER]: FREE_TIER_USER_CREDITS,
    [OrgRole.EDITOR]: FREE_TIER_USER_CREDITS,
    [OrgRole.EXPLORER]: 0,
  },
  [FeatureGateTier.PROFESSIONAL]: {
    [OrgRole.ADMIN]: 30,
    [OrgRole.MANAGER]: 30,
    [OrgRole.EDITOR]: 30,
    [OrgRole.EXPLORER]: 10,
  },
  [FeatureGateTier.TEAM]: {
    [OrgRole.ADMIN]: 40,
    [OrgRole.MANAGER]: 40,
    [OrgRole.EDITOR]: 40,
    [OrgRole.EXPLORER]: 10,
  },
  [FeatureGateTier.ENTERPRISE]: {
    [OrgRole.ADMIN]: 60,
    [OrgRole.MANAGER]: 60,
    [OrgRole.EDITOR]: 60,
    [OrgRole.EXPLORER]: 10,
  },
};

export const TIER_ORDERING = [
  FeatureGateTier.COMMUNITY,
  FeatureGateTier.PROFESSIONAL,
  FeatureGateTier.TEAM,
  FeatureGateTier.ENTERPRISE,
];

/**
 * Compares tier in terms of "pricing" order specified in TIER_ORDERING.
 *
 * Returns a postive value if compared tier is an "upgrade".
 *
 * Returns a negative value if compared tier is a "downgrade".
 *
 * Returns 0 if tiers are equal.
 */
export const compareTiers = ({
  comparingTier,
  currentTier,
}: {
  currentTier: FeatureGateTier;
  comparingTier: FeatureGateTier;
}): number => {
  return (
    TIER_ORDERING.indexOf(comparingTier) - TIER_ORDERING.indexOf(currentTier)
  );
};

export const isTierSuperSet = (
  tier: FeatureGateTier,
  targetTier: FeatureGateTier,
): boolean => {
  if (TIER_ORDERING.indexOf(tier) === -1) {
    return false;
  }

  return TIER_ORDERING.indexOf(tier) >= TIER_ORDERING.indexOf(targetTier);
};

export const FeatureGateClickedSourceLiteral = z.enum([
  "BANNER",
  "FEATURE_DISABLED",
]);

export const FeatureGateClickedSource = getNormalEnum(
  FeatureGateClickedSourceLiteral,
);
export type FeatureGateClickedSource = z.infer<
  typeof FeatureGateClickedSourceLiteral
>;

export const FeatureGateCtaLiteral = z.enum(["CONTACT_SALES", "VIEW_PLANS"]);

export const FeatureGateCta = getNormalEnum(FeatureGateCtaLiteral);
export type FeatureGateCta = z.infer<typeof FeatureGateCtaLiteral>;

export const FeatureGateClickedEventData = z.object({
  triggeredFrom: FeatureGateClickedSourceLiteral,
  featureGateType: z.array(FeatureGateNameWithAddons),
  orgRole: OrgRoleLiteral.optional(),
  cta: FeatureGateCtaLiteral.optional(),
});

export type FeatureGateClickedEventData = z.infer<
  typeof FeatureGateClickedEventData
>;
